import type { ReactNode } from 'react'
import HelpPopover from '../help/HelpPopover'
import type { WorkflowStep } from '../ui/WorkflowStepper'

/**
 * The invention pipeline strip, with the LOCK stated (figure S3).
 *
 * The strip itself is not new — the project page has shown "Workflow Progress"
 * for a long time. What the design is actually about is the thing that was
 * missing: Matching does not open until the review is approved. Without the
 * sentence, a user who reaches Review and sees Matching sitting there greyed
 * out has no way to tell an unfinished feature from a deliberate gate, and the
 * honest answer — that they hold the key and one click turns it — is invisible.
 *
 * The lock is conditioned on the review state and on NOTHING else. A second
 * condition (assessment scores, document count, matching state) would make the
 * caption disappear on a project whose review is genuinely pending, which is
 * both wrong for the user and a silent break of the S3 gate. The approve
 * mutation stays on the page that owns it and arrives here as `action`.
 */
export interface PipelineStepperProps {
  steps: WorkflowStep[]
  /** True once the project's review_status is 'approved'. */
  reviewApproved: boolean
  /** The owner's approve control, rendered by the page that owns the mutation. */
  action?: ReactNode
  /** UX-018: opens the explanation of the lock (the Matches tab's locked card). */
  onExplainLock?: () => void
}

const LOCK_REASON = 'Locked until review is approved'
/** UX-019: the caption on the step itself is one word; the full sentence sits under the strip. */
const LOCK_CAPTION = 'Locked'
const LOCK_SENTENCE_ID = 'pipeline-lock-reason'

function caption(step: WorkflowStep, reviewApproved: boolean): { text: string; cls: string } {
  if (step.status === 'completed') return { text: 'Done', cls: 'text-emerald-700' }
  if (step.status === 'active') return { text: "You're here", cls: 'text-iflow-700' }
  if (step.status === 'locked' || (step.label === 'Matching' && !reviewApproved)) {
    return { text: LOCK_CAPTION, cls: 'text-gray-600' }
  }
  return { text: 'Later', cls: 'text-gray-600' }
}

function dot(step: WorkflowStep, locked: boolean): { cls: string; glyph: string } {
  if (step.status === 'completed') return { cls: 'border-emerald-500 bg-emerald-500 text-white', glyph: '✓' }
  if (step.status === 'active') return { cls: 'border-iflow-500 bg-iflow-500 text-white ring-4 ring-iflow-100', glyph: '●' }
  if (locked) return { cls: 'border-dashed border-slate-300 bg-slate-100 text-gray-600', glyph: '🔒' }
  return { cls: 'border-slate-300 bg-white text-gray-600', glyph: '○' }
}

export default function PipelineStepper({ steps, reviewApproved, action, onExplainLock }: PipelineStepperProps) {
  const active = steps.find((s) => s.status === 'active')
  const stateLabel = active
    ? `${active.label} · ${active.label === 'Review' && !reviewApproved ? 'approve to continue' : 'in progress'}`
    : 'Every stage complete'

  return (
    <div data-part="PipelineStepper" className="light-card mb-4 p-4">
      <div className="mb-2 flex items-center justify-between gap-3">
        <h3 className="flex items-center gap-1.5 text-[13px] font-bold text-iflow-900">
          <span aria-hidden>🛤</span> <span>Workflow Progress</span>
          <HelpPopover
            topic="concept-pipeline"
            title="The pipeline"
            body="Every invention follows the same path: Upload → Intake → Analysis → Assessment → Review → Matching → Disclosure."
            size={14}
          />
        </h3>
        <span className="shrink-0 rounded-full bg-iflow-100 px-2.5 py-0.5 text-[12px] font-semibold text-iflow-700">
          {stateLabel}
        </span>
      </div>

      <ol aria-label="Project pipeline" className="flex flex-col gap-y-3 sm:flex-row sm:flex-wrap sm:items-start">
        {steps.map((step, i) => {
          const locked = step.status === 'locked' || (step.label === 'Matching' && !reviewApproved && step.status !== 'completed')
          const cap = caption(step, reviewApproved)
          const face = dot(step, locked)
          const clickable = !!step.onSelect && !locked
          const Tag = clickable || locked ? 'button' : 'div'
          return (
            <li
              key={step.key}
              className="flex items-start sm:flex-1"
              aria-current={step.status === 'active' ? 'step' : undefined}
              data-testid={`pipeline-step-${step.key}`}
              data-status={locked ? 'locked' : step.status}
            >
              <div className="flex w-full flex-col sm:w-24 sm:shrink-0 sm:items-center">
                <Tag
                  type={clickable || locked ? 'button' : undefined}
                  // A locked step stays focusable (aria-disabled, not disabled) so a
                  // keyboard or touch user can land on it and read the reason; it
                  // opens the explanation rather than navigating.
                  aria-disabled={locked ? true : undefined}
                  aria-describedby={locked ? LOCK_SENTENCE_ID : undefined}
                  onClick={clickable ? step.onSelect : locked && onExplainLock ? onExplainLock : undefined}
                  title={locked ? `${step.label} — ${LOCK_REASON}` : undefined}
                  className={`flex w-full items-center gap-3 text-left sm:flex-col sm:gap-0 sm:text-center ${clickable || locked ? 'cursor-pointer' : 'cursor-default'}`}
                >
                  <span className={`flex h-7.5 w-7.5 shrink-0 items-center justify-center rounded-full border-2 p-1 text-[12px] font-bold ${face.cls}`}>
                    <span aria-hidden>{face.glyph}</span>
                  </span>
                  <span className="flex flex-col sm:items-center">
                    <span className={`sm:mt-1.5 text-[12px] leading-tight ${step.status === 'active' ? 'font-bold text-iflow-900' : 'font-semibold text-gray-700'}`}>
                      {step.label}
                    </span>
                    <span className={`mt-0.5 text-[12px] font-semibold tracking-wide ${cap.cls}`}>{cap.text}</span>
                  </span>
                </Tag>
                {locked && step.label === 'Matching' && onExplainLock && (
                  <span
                    role="button"
                    tabIndex={0}
                    data-testid="why-locked"
                    onClick={onExplainLock}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onExplainLock() } }}
                    className="mt-1 cursor-pointer text-[12px] font-semibold text-iflow-700 underline hover:text-iflow-900 sm:text-center"
                  >
                    Why is this locked?
                  </span>
                )}
              </div>
              {i < steps.length - 1 && (
                <span
                  aria-hidden
                  className={`mt-3.5 hidden h-0.5 flex-1 sm:block ${step.status === 'completed' ? 'bg-emerald-300' : 'bg-slate-200'}`}
                />
              )}
            </li>
          )
        })}
      </ol>

      {!reviewApproved && (
        // UX-019: the step itself says only "Locked"; the full sentence is
        // stated here, visibly, under the strip - the same fact the S3 contract
        // row pins, and what the locked step's aria-describedby points at.
        <p id={LOCK_SENTENCE_ID} className="mt-3 text-[12px] text-gray-700">Matching — {LOCK_REASON}</p>
      )}
      {action}
    </div>
  )
}
